import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, getDoc, updateDoc, deleteDoc, addDoc, collection } from 'firebase/firestore';
import shield from '../assets/shortley-shield.png';
import theme, { getTheme } from '../theme';
import { useDarkMode } from '../context/DarkModeContext';
import useIsMobile from '../hooks/useIsMobile';

const QUALS = [
  { id:'AP', label:'AP', flag:'🇺🇸', desc:'Advanced Placement' },
  { id:'SAT', label:'SAT', flag:'', desc:'College Board SAT' },
  { id:'ACT', label:'ACT', flag:'', desc:'ACT Exam' },
  { id:'IB', label:'IB', flag:'', desc:'International Baccalaureate' },
  { id:'A-Level', label:'A-Level', flag:'🇬🇧', desc:'UK A-Levels' },
  { id:'GCSE', label:'GCSE', flag:'', desc:'UK GCSEs' },
  { id:'IGCSE', label:'IGCSE', flag:'', desc:'International GCSE' },
  { id:'CBSE', label:'CBSE', flag:'🇮🇳', desc:'Indian CBSE' },
  { id:'Custom', label:'Custom', flag:'', desc:'Other qualification' },
];

const YEARS = ['Grade 9','Grade 10','Grade 11','Grade 12','Year 10','Year 11','Year 12','Year 13','University Year 1','University Year 2','University Year 3','Other'];

const GOALS = [
  { id:'top_grades', icon:'', label:'Get top grades' },
  { id:'fill_gaps', icon:'', label:'Fill curriculum gaps' },
  { id:'exam_prep', icon:'', label:'Prepare for upcoming exams' },
  { id:'stay_ahead', icon:'', label:'Stay ahead of class' },
  { id:'catch_up', icon:'', label:'Catch up on missed content' },
  { id:'university', icon:'', label:'University applications' },
];

const STUDY_STYLES = [
  { id:'visual', icon:'', label:'Visual learner', desc:'Diagrams, charts, colour coding' },
  { id:'practice', icon:'', label:'Practice questions', desc:'Learn by doing and testing' },
  { id:'reading', icon:'', label:'Reading and notes', desc:'Detailed notes and summaries' },
  { id:'video', icon:'', label:'Video explanations', desc:'Watch then apply' },
];

// Subjects offered per qualification during onboarding (OB-1). More can always
// be added from the Dashboard afterwards.
const SUBJECT_SUGGESTIONS = {
  AP: ['AP Calculus AB', 'AP Calculus BC', 'AP Physics 1', 'AP Chemistry', 'AP Biology', 'AP Statistics', 'AP US History', 'AP English Language', 'AP Psychology', 'AP Computer Science A', 'AP Macroeconomics'],
  GCSE: ['Maths', 'English Language', 'English Literature', 'Biology', 'Chemistry', 'Physics', 'History', 'Geography', 'Computer Science'],
  'A-Level': ['Maths', 'Further Maths', 'Physics', 'Chemistry', 'Biology', 'Economics', 'English Literature', 'History', 'Psychology'],
  IB: ['Mathematics AA', 'Mathematics AI', 'Physics', 'Chemistry', 'Biology', 'Economics', 'English A Literature', 'History', 'Psychology'],
  SAT: ['SAT'],
  ACT: ['ACT'],
};
const UK_BOARDS = ['AQA', 'Edexcel', 'OCR'];
const SUBJECT_COLORS = ['#d4a843', '#3dba6e', '#8b6fd4', '#4a6fa5', '#e05252', '#0f766e'];
const PREFERRED_TIMES = [
  { id: 'morning', label: 'Morning', desc: 'Before school' },
  { id: 'afternoon', label: 'Afternoon', desc: 'Right after school' },
  { id: 'evening', label: 'Evening', desc: 'After dinner' },
  { id: 'late', label: 'Late night', desc: 'When it\'s quiet' },
];

const STEPS = ['Account','Profile','Studying','Exam dates','Goals'];
const ACCOUNT_TYPES = [
  { id: 'learner', icon: '', label: 'Student', desc: 'I want to study and improve my grades' },
  { id: 'educator', icon: '', label: 'Teacher / Parent', desc: 'I want to monitor and support learners' },
];

export default function Signup() {
  useEffect(() => { document.title = 'Sign Up — Learnable'; return () => { document.title = 'Learnable'; }; }, []);
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [dob, setDob] = useState('');
  const [school, setSchool] = useState('');
  const [country, setCountry] = useState('');
  const [yearGroup, setYearGroup] = useState('');
  const [quals, setQuals] = useState([]);
  const [goals, setGoals] = useState([]);
  const [studyStyle, setStudyStyle] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState('5-10');
  const [accountType, setAccountType] = useState('learner');
  const [pickedSubjects, setPickedSubjects] = useState([]); // [{ name, qualification, examBoard }]
  const [ukBoard, setUkBoard] = useState('AQA');
  const [examDates, setExamDates] = useState({}); // subject name → yyyy-mm-dd
  const [preferredTime, setPreferredTime] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OB-1: progress survives closing the tab (everything except the password).
  const SIGNUP_PROGRESS_KEY = 'learnable_signup_progress';
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SIGNUP_PROGRESS_KEY);
      if (!raw) return;
      const p = JSON.parse(raw);
      if (p.name) setName(p.name);
      if (p.email) setEmail(p.email);
      if (p.dob) setDob(p.dob);
      if (p.school) setSchool(p.school);
      if (p.country) setCountry(p.country);
      if (p.yearGroup) setYearGroup(p.yearGroup);
      if (p.quals?.length) setQuals(p.quals);
      if (p.goals?.length) setGoals(p.goals);
      if (p.studyStyle) setStudyStyle(p.studyStyle);
      if (p.hoursPerWeek) setHoursPerWeek(p.hoursPerWeek);
      if (p.pickedSubjects?.length) setPickedSubjects(p.pickedSubjects);
      if (p.examDates) setExamDates(p.examDates);
      if (p.preferredTime) setPreferredTime(p.preferredTime);
      if (p.step) setStep(Math.min(p.step, STEPS.length - 1));
    } catch (e) { /* corrupt progress — start fresh */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (step === 0 && !name && !email) return;
    try {
      localStorage.setItem(SIGNUP_PROGRESS_KEY, JSON.stringify({
        step, name, email, dob, school, country, yearGroup, quals, goals,
        studyStyle, hoursPerWeek, pickedSubjects, examDates, preferredTime,
      }));
    } catch (e) { /* storage full — non-fatal */ }
  }, [step, name, email, dob, school, country, yearGroup, quals, goals, studyStyle, hoursPerWeek, pickedSubjects, examDates, preferredTime]);

  const toggleSubjectPick = (subjectName, qualification) => {
    setPickedSubjects(prev => {
      const exists = prev.find(s => s.name === subjectName && s.qualification === qualification);
      if (exists) return prev.filter(s => !(s.name === subjectName && s.qualification === qualification));
      const needsBoard = qualification === 'GCSE' || qualification === 'A-Level';
      return [...prev, { name: subjectName, qualification, examBoard: needsBoard ? ukBoard : '' }];
    });
  };
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isDark } = useDarkMode();
  const th = getTheme(isDark);

  const bg = th.bg;
  const gold = th.gold;
  const border = th.border2;
  const sub = th.textTert;
  const card = th.surface;

  const inp = {
    width:'100%', padding:'12px 16px',
    backgroundColor: th.surface2,
    border:`1px solid ${border}`, borderRadius:'11px',
    color: th.text, fontSize:'16px', outline:'none',
    boxSizing:'border-box', transition:'border-color 0.2s',
    fontFamily:'inherit',
  };

  const toggleQual = (id) => {
    setQuals(prev => prev.includes(id) ? prev.filter(q => q !== id) : [...prev, id]);
  };
  const toggleGoal = (id) => {
    setGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const validateStep = () => {
    setError('');
    if (step === 0) {
      if (!name.trim()) { setError('Please enter your full name.'); return false; };
      if (!email.includes('@')) { setError('Please enter a valid email address.'); return false; };
      if (password.length < 6) { setError('Password must be at least 6 characters.'); return false; };
      return true;
    }
    if (step === 1) return true;
    if (step === 2) {
      if (quals.length === 0) { setError('Please select at least one qualification.'); return false; };
      return true;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else handleSignup();
  };

  const handleSignup = async () => {
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      await setDoc(doc(db, 'users', cred.user.uid), {
        name, email, dob, school, country, yearGroup,
        qualifications: quals, goals, studyStyle, hoursPerWeek, preferredStudyTime: preferredTime,
        accountType,
        createdAt: new Date().toISOString(),
        uid: cred.user.uid,
        isNewUser: true,
        plan: 'free',
        credits: 50,
      });
      // OB-1/OB-2: chosen subjects and exam dates become real documents so the
      // first Dashboard is personalised from minute one, and Cailyn knows the
      // student's learning style immediately.
      try {
        await setDoc(doc(db, 'userProfile', cred.user.uid), {
          displayName: name, school,
          learningStyle: studyStyle || '',
          preferredStudyTime: preferredTime || '',
          studyHoursPerDay: hoursPerWeek || '',
          createdAt: new Date().toISOString(),
        }, { merge: true });
        await Promise.all(pickedSubjects.map((s, i) =>
          addDoc(collection(db, 'subjects'), {
            name: s.name, qualification: s.qualification, examBoard: s.examBoard || '',
            color: SUBJECT_COLORS[i % SUBJECT_COLORS.length],
            userId: cred.user.uid, createdAt: new Date().toISOString(),
          })
        ));
        await Promise.all(pickedSubjects.filter(s => examDates[s.name]).map(s =>
          addDoc(collection(db, 'exams'), {
            subject: s.name, examName: `${s.qualification} ${s.name}`,
            date: examDates[s.name],
            userId: cred.user.uid, createdAt: new Date().toISOString(),
          })
        ));
      } catch (setupErr) { console.error('onboarding setup write failed', setupErr); }
      localStorage.removeItem(SIGNUP_PROGRESS_KEY);
      // Check if email has pending Pro activation (paid before signing up)
      try {
        const pendingRef = doc(db, 'pendingPro', email);
        const pendingSnap = await getDoc(pendingRef);
        if (pendingSnap.exists()) {
          await updateDoc(doc(db, 'users', cred.user.uid), {
            plan: pendingSnap.data().plan || 'pro',
            proActivatedAt: new Date().toISOString(),
            proSource: 'stripe_pending',
          });
          await deleteDoc(pendingRef);
        }
      } catch(e) {  }
      navigate('/dashboard?welcome=1');
    } catch (err) {
      setError(
        err.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists. Try signing in instead.'
          : err.code === 'auth/weak-password'
          ? 'Password is too weak. Use at least 6 characters.'
          : 'Something went wrong. Please try again.'
      );
      setStep(0);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:'100vh', backgroundColor:bg, fontFamily:"'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif", color: th.text, display:'flex', flexDirection:'column' }}>
      {/* Nav */}
      <nav style={{ padding:'0 32px', height:'58px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:`1px solid ${border}`, flexShrink:0 }}>
        <Link to="/" style={{ display:'flex', alignItems:'center', gap:'10px', textDecoration:'none' }}>
          <img src={shield} alt="" style={{ width:'32px', height:'32px', objectFit:'contain' }} />
          <span style={{ fontSize:'16px', fontWeight:'700', color: th.text, letterSpacing:'-0.01em' }}>Learnable</span>
        </Link>
        <Link to="/login" style={{ fontSize:'13px', color:sub, textDecoration:'none' }}>
          Already have an account? <span style={{ color:gold, fontWeight:'600' }}>Sign in →</span>
        </Link>
      </nav>

      <div style={{ flex:1, display:'flex', flexDirection: isMobile ? 'column' : 'row', padding: isMobile ? '20px 16px' : '32px 16px', gap: isMobile ? '20px' : '48px', margin:'0 auto', width:'100%', alignItems: isMobile ? 'stretch' : 'flex-start', justifyContent:'center' }}>

        {/* Left side — progress and features (hidden on mobile; the in-card step counter covers this) */}
        {!isMobile && (
        <div style={{ width:'280px', flexShrink:0, display:'flex', flexDirection:'column', gap:'0', paddingTop:'8px' }}>
          <div style={{ marginBottom:'32px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:sub, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'16px' }}>Setup progress</div>
            {STEPS.map((s, i) => (
              <div key={s} style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px' }}>
                <div style={{ width:'28px', height:'28px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:'700', flexShrink:0, transition:'all 0.3s',
                  backgroundColor: i < step ? gold : i === step ? th.goldGlow : th.surface2,
                  border: i === step ? `2px solid ${gold}` : i < step ? 'none' : `1px solid ${border}`,
                  color: i < step ? '#0e0c0a' : i === step ? gold : sub,
                }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <div>
                  <div style={{ fontSize:'13px', fontWeight: i === step ? '700' : '400', color: i === step ? th.text : i < step ? th.green : sub }}>{s}</div>
                  {i === step && <div style={{ fontSize:'11px', color:sub, marginTop:'1px' }}>
                    {i===0?'Create your account':i===1?'Tell us about yourself':i===2?'What are you studying?':'Set your learning goals'}
                  </div>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding:'16px', backgroundColor:th.goldGlow, border:`1px solid ${th.goldBorder}`, borderRadius:'14px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:gold, marginBottom:'10px' }}>What you get free</div>
            {['Curriculum gap map for all subjects','AI Tutor with 50 monthly credits','Question Rush practice mode','Full past papers library','Study planner and analytics'].map(f => (
              <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:'8px', marginBottom:'7px' }}>
                <span style={{ color: th.green, fontSize:'12px', marginTop:'1px', flexShrink:0 }}>✓</span>
                <span style={{ fontSize:'12px', color: th.textSec, lineHeight:1.4 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Right side — form */}
        <div style={{ flex:1, maxWidth: isMobile ? 'none' : '500px', width: '100%' }}>
          {/* Progress bar */}
          <div style={{ marginBottom:'24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
              <span style={{ fontSize:'13px', fontWeight:'700', color: th.text }}>{STEPS[step]}</span>
              <span style={{ fontSize:'12px', color:sub }}>Step {step+1} of {STEPS.length}</span>
            </div>
            <div style={{ height:'4px', backgroundColor:th.border, borderRadius:'2px', overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${((step+1)/STEPS.length)*100}%`, background:`linear-gradient(90deg,${gold},#a07828)`, borderRadius:'2px', transition:'width 0.4s ease' }}></div>
            </div>
          </div>

          <div style={{ backgroundColor:card, border:`1px solid ${border}`, borderRadius:'20px', padding: isMobile ? '22px' : '32px', boxShadow: th.shadow3 }}>
            {error && (
              <div style={{ padding:'12px 16px', backgroundColor:th.redGlow, border:'1px solid rgba(224,82,82,0.2)', borderRadius:'10px', fontSize:'13px', color: th.red, marginBottom:'20px', display:'flex', alignItems:'center', gap:'8px' }}>
                 {error}
              </div>
            )}

            {/* STEP 0 — Account */}
            {step === 0 && (
              <div style={{ display:'flex', flexDirection:'column', gap:'18px' }}>
                <div>
                  <h2 style={{ fontSize:'22px', fontWeight:'700', fontFamily:"'DM Serif Display',Georgia,serif", color: th.text, marginBottom:'4px', letterSpacing:'-0.02em' }}>Create your account</h2>
                  <p style={{ fontSize:'13px', color:sub }}>Free forever. No credit card required.</p>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'10px' }}>I am a...</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'4px' }}>
                    {ACCOUNT_TYPES.map(acct => (
                      <button key={acct.id} type="button" onClick={() => setAccountType(acct.id)}
                        style={{ padding:'14px 12px', borderRadius:'12px', border:`1px solid ${accountType===acct.id ? th.goldBorder : border}`, cursor:'pointer', textAlign:'left', backgroundColor:accountType===acct.id?th.goldGlow:th.surface2, transition:'all 0.15s' }}>
                        <div style={{ fontSize:'22px', marginBottom:'5px' }}>{acct.icon}</div>
                        <div style={{ fontSize:'13px', fontWeight:'700', color:accountType===acct.id?gold:th.text, marginBottom:'2px' }}>{acct.label}</div>
                        <div style={{ fontSize:'11px', color:sub, lineHeight:1.4 }}>{acct.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Full Name</label>
                  <input autoFocus type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="Your full name" style={inp}
                    onFocus={e=>e.target.style.borderColor=gold} onBlur={e=>e.target.style.borderColor=border} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Email Address</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@email.com" style={inp}
                    onFocus={e=>e.target.style.borderColor=gold} onBlur={e=>e.target.style.borderColor=border} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Password</label>
                  <div style={{ position:'relative' }}>
                    <input type={showPw?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)} placeholder="Min 6 characters" style={{...inp, paddingRight:'50px'}}
                      onFocus={e=>e.target.style.borderColor=gold} onBlur={e=>e.target.style.borderColor=border} />
                    <button type="button" onClick={()=>setShowPw(p=>!p)} style={{ position:'absolute', right:'14px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:sub, cursor:'pointer', fontSize:'12px', padding:'4px' }}>
                      {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div style={{ marginTop:'6px', display:'flex', gap:'4px' }}>
                      {[1,2,3,4].map(i => (
                        <div key={i} style={{ flex:1, height:'3px', borderRadius:'2px', backgroundColor: password.length >= i*3 ? (password.length >= 12 ? th.green : password.length >= 8 ? th.amber : th.red) : th.border }}></div>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={next} style={{ width:'100%', padding:'14px', borderRadius:'12px', fontSize:'15px', fontWeight:'700', background:`linear-gradient(135deg,${gold},#b8872d)`, color:'#0e0c0a', border:'none', cursor:'pointer', marginTop:'4px', boxShadow:'0 4px 20px rgba(212,168,67,0.3)' }}>
                  Continue →
                </button>
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap:'6px' }}>
                  {[['','Secure'],['🆓','Free forever'],['','Setup in 60s']].map(([icon, label]) => (
                    <div key={label} style={{ textAlign:'center', padding:'8px', backgroundColor:th.surface2, borderRadius:'8px', border:`1px solid ${border}` }}>
                      <div style={{ fontSize:'14px', marginBottom:'2px' }}>{icon}</div>
                      <div style={{ fontSize:'10px', color:sub, fontWeight:'600' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 1 — Profile */}
            {step === 1 && (
              <div style={{ display:'flex', flexDirection:'column', gap:'18px' }}>
                <div>
                  <h2 style={{ fontSize:'22px', fontWeight:'700', fontFamily:"'DM Serif Display',Georgia,serif", color: th.text, marginBottom:'4px' }}>Tell us about yourself</h2>
                  <p style={{ fontSize:'13px', color:sub }}>This helps us personalise your experience. All optional.</p>
                </div>
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'14px' }}>
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>School / College</label>
                    <input type="text" value={school} onChange={e=>setSchool(e.target.value)} placeholder="Your school name" style={inp}
                      onFocus={e=>e.target.style.borderColor=gold} onBlur={e=>e.target.style.borderColor=border} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Country</label>
                    <input type="text" value={country} onChange={e=>setCountry(e.target.value)} placeholder="United States" style={inp}
                      onFocus={e=>e.target.style.borderColor=gold} onBlur={e=>e.target.style.borderColor=border} />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'14px' }}>
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Year / Grade</label>
                    <select value={yearGroup} onChange={e=>setYearGroup(e.target.value)} style={{...inp, cursor:'pointer'}}>
                      <option value="">Select year...</option>
                      {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Date of Birth</label>
                    <input type="date" value={dob} onChange={e=>setDob(e.target.value)} style={{...inp, colorScheme: isDark ? 'dark' : 'light'}}
                      onFocus={e=>e.target.style.borderColor=gold} onBlur={e=>e.target.style.borderColor=border} />
                  </div>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>How many hours per week do you study?</label>
                  <div style={{ display:'flex', gap:'6px' }}>
                    {['<5','5-10','10-15','15-20','20+'].map(h => (
                      <button key={h} type="button" onClick={()=>setHoursPerWeek(h)}
                        style={{ flex:1, padding:'9px 4px', borderRadius:'9px', fontSize:'12px', fontWeight:'600', border:'none', cursor:'pointer', backgroundColor:hoursPerWeek===h?gold:th.surface2, color:hoursPerWeek===h?'#0e0c0a':sub, transition:'all 0.15s' }}>
                        {h}h
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'8px', marginTop:'4px' }}>
                  <button onClick={()=>setStep(0)} style={{ flex:1, padding:'13px', borderRadius:'12px', fontSize:'14px', fontWeight:'600', backgroundColor:'transparent', border:`1px solid ${border}`, color:sub, cursor:'pointer' }}>← Back</button>
                  <button onClick={next} style={{ flex:2, padding:'13px', borderRadius:'12px', fontSize:'15px', fontWeight:'700', background:`linear-gradient(135deg,${gold},#b8872d)`, color:'#0e0c0a', border:'none', cursor:'pointer', boxShadow:'0 4px 20px rgba(212,168,67,0.3)' }}>Continue →</button>
                </div>
              </div>
            )}

            {/* STEP 2 — Qualifications */}
            {step === 2 && (
              <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
                <div>
                  <h2 style={{ fontSize:'22px', fontWeight:'700', fontFamily:"'DM Serif Display',Georgia,serif", color: th.text, marginBottom:'4px' }}>What are you studying?</h2>
                  <p style={{ fontSize:'13px', color:sub }}>Select all that apply. You can add more subjects after signup.</p>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'10px' }}>Qualifications — select all that apply</label>
                  <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap:'8px' }}>
                    {QUALS.map(q => {
                      const selected = quals.includes(q.id);
                      return (
                        <button key={q.id} type="button" onClick={()=>toggleQual(q.id)}
                          style={{ padding:'12px 8px', borderRadius:'12px', border:`1px solid ${selected ? th.goldBorder : border}`, cursor:'pointer', textAlign:'center', backgroundColor:selected?th.goldGlow:th.surface2, transition:'all 0.15s' }}>
                          <div style={{ fontSize:'20px', marginBottom:'4px' }}>{q.flag}</div>
                          <div style={{ fontSize:'13px', fontWeight:'700', color:selected?gold:th.text }}>{q.label}</div>
                          <div style={{ fontSize:'10px', color:sub, marginTop:'2px' }}>{q.desc}</div>
                          {selected && <div style={{ fontSize:'10px', color:gold, marginTop:'4px', fontWeight:'700' }}>✓ Selected</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {quals.some(q => SUBJECT_SUGGESTIONS[q]?.length) && (
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Pick your subjects</label>
                    {quals.some(q => q === 'GCSE' || q === 'A-Level') && (
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                        <span style={{ fontSize:'12px', color:sub }}>Exam board:</span>
                        {UK_BOARDS.map(b => (
                          <button key={b} type="button"
                            onClick={()=>{ setUkBoard(b); setPickedSubjects(prev => prev.map(s => (s.qualification==='GCSE'||s.qualification==='A-Level') ? { ...s, examBoard: b } : s)); }}
                            style={{ padding:'5px 12px', borderRadius:'16px', fontSize:'11px', fontWeight:'700', border:'none', cursor:'pointer', backgroundColor:ukBoard===b?gold:th.surface2, color:ukBoard===b?'#0e0c0a':sub }}>
                            {b}
                          </button>
                        ))}
                      </div>
                    )}
                    {quals.filter(q => SUBJECT_SUGGESTIONS[q]?.length).map(q => (
                      <div key={q} style={{ marginBottom:'10px' }}>
                        <div style={{ fontSize:'10px', fontWeight:'700', color:sub, marginBottom:'5px' }}>{q}</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                          {SUBJECT_SUGGESTIONS[q].map(name => {
                            const picked = pickedSubjects.some(s => s.name === name && s.qualification === q);
                            return (
                              <button key={name} type="button" onClick={()=>toggleSubjectPick(name, q)}
                                style={{ padding:'7px 13px', borderRadius:'18px', fontSize:'12px', fontWeight:'600', cursor:'pointer', border:`1px solid ${picked ? th.goldBorder : border}`, backgroundColor:picked?th.goldGlow:th.surface2, color:picked?gold:th.text, transition:'all 0.12s' }}>
                                {picked ? '✓ ' : ''}{name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>Preferred learning style</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    {STUDY_STYLES.map(s => (
                      <button key={s.id} type="button" onClick={()=>setStudyStyle(s.id)}
                        style={{ padding:'12px', borderRadius:'11px', border:`1px solid ${studyStyle===s.id ? th.goldBorder : border}`, cursor:'pointer', textAlign:'left', backgroundColor:studyStyle===s.id?th.goldGlow:th.surface2, transition:'all 0.15s' }}>
                        <div style={{ fontSize:'12px', fontWeight:'700', color:studyStyle===s.id?gold:th.text }}>{s.label}</div>
                        <div style={{ fontSize:'10px', color:sub, marginTop:'2px' }}>{s.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'8px' }}>
                  <button onClick={()=>setStep(1)} style={{ flex:1, padding:'13px', borderRadius:'12px', fontSize:'14px', fontWeight:'600', backgroundColor:'transparent', border:`1px solid ${border}`, color:sub, cursor:'pointer' }}>← Back</button>
                  <button onClick={next} style={{ flex:2, padding:'13px', borderRadius:'12px', fontSize:'15px', fontWeight:'700', background:`linear-gradient(135deg,${gold},#b8872d)`, color:'#0e0c0a', border:'none', cursor:'pointer', boxShadow:'0 4px 20px rgba(212,168,67,0.3)' }}>Continue →</button>
                </div>
              </div>
            )}

            {/* STEP 3 — Exam dates (skippable) */}
            {step === 3 && (
              <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
                <div>
                  <h2 style={{ fontSize:'22px', fontWeight:'700', fontFamily:"'DM Serif Display',Georgia,serif", color: th.text, marginBottom:'4px' }}>When are your exams?</h2>
                  <p style={{ fontSize:'13px', color:sub }}>Optional — but with dates, your study plan and countdowns work from day one. You can skip this.</p>
                </div>
                {pickedSubjects.length === 0 ? (
                  <p style={{ fontSize:'13px', color:sub, padding:'16px', backgroundColor:th.surface2, borderRadius:'12px' }}>No subjects picked yet — go back to add some, or skip and set dates later in the Study Planner.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                    {pickedSubjects.map(s => (
                      <div key={`${s.qualification}_${s.name}`} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', backgroundColor:th.surface2, borderRadius:'11px', border:`1px solid ${border}` }}>
                        <span style={{ flex:1, fontSize:'13px', fontWeight:'600', color:th.text }}>{s.name} <span style={{ color:sub, fontWeight:400 }}>({s.qualification})</span></span>
                        <input type="date" value={examDates[s.name] || ''} onChange={e=>setExamDates(prev=>({ ...prev, [s.name]: e.target.value }))}
                          style={{ padding:'8px 10px', borderRadius:'8px', border:`1px solid ${border}`, backgroundColor:th.surface, color:th.text, fontSize:'13px', outline:'none' }} />
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:'8px' }}>
                  <button onClick={()=>setStep(2)} style={{ flex:1, padding:'13px', borderRadius:'12px', fontSize:'14px', fontWeight:'600', backgroundColor:'transparent', border:`1px solid ${border}`, color:sub, cursor:'pointer' }}>← Back</button>
                  <button onClick={()=>setStep(4)} style={{ flex:1, padding:'13px', borderRadius:'12px', fontSize:'14px', fontWeight:'600', backgroundColor:'transparent', border:`1px solid ${border}`, color:sub, cursor:'pointer' }}>Skip for now</button>
                  <button onClick={next} style={{ flex:2, padding:'13px', borderRadius:'12px', fontSize:'15px', fontWeight:'700', background:`linear-gradient(135deg,${gold},#b8872d)`, color:'#0e0c0a', border:'none', cursor:'pointer', boxShadow:'0 4px 20px rgba(212,168,67,0.3)' }}>Continue →</button>
                </div>
              </div>
            )}

            {/* STEP 4 — Goals */}
            {step === 4 && (
              <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
                <div>
                  <h2 style={{ fontSize:'22px', fontWeight:'700', fontFamily:"'DM Serif Display',Georgia,serif", color: th.text, marginBottom:'4px' }}>What are your goals?</h2>
                  <p style={{ fontSize:'13px', color:sub }}>Select all that apply. This shapes your personalised plan.</p>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'10px' }}>I want to...</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    {GOALS.map(g => {
                      const selected = goals.includes(g.id);
                      return (
                        <button key={g.id} type="button" onClick={()=>toggleGoal(g.id)}
                          style={{ padding:'14px 12px', borderRadius:'12px', border:`1px solid ${selected ? th.goldBorder : border}`, cursor:'pointer', textAlign:'left', backgroundColor:selected?th.goldGlow:th.surface2, transition:'all 0.15s', display:'flex', alignItems:'center', gap:'10px' }}>
                          <span style={{ fontSize:'20px', flexShrink:0 }}>{g.icon}</span>
                          <div>
                            <div style={{ fontSize:'13px', fontWeight:'600', color:selected?gold:th.text }}>{g.label}</div>
                          </div>
                          {selected && <span style={{ marginLeft:'auto', color:gold, fontSize:'14px', flexShrink:0 }}>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em', color:sub, marginBottom:'7px' }}>When do you study best?</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    {PREFERRED_TIMES.map(p => (
                      <button key={p.id} type="button" onClick={()=>setPreferredTime(p.id)}
                        style={{ padding:'12px', borderRadius:'11px', border:`1px solid ${preferredTime===p.id ? th.goldBorder : border}`, cursor:'pointer', textAlign:'left', backgroundColor:preferredTime===p.id?th.goldGlow:th.surface2, transition:'all 0.15s' }}>
                        <div style={{ fontSize:'12px', fontWeight:'700', color:preferredTime===p.id?gold:th.text }}>{p.label}</div>
                        <div style={{ fontSize:'10px', color:sub, marginTop:'2px' }}>{p.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ padding:'14px 16px', backgroundColor:th.greenGlow, border:`1px solid rgba(61,186,110,0.2)`, borderRadius:'12px' }}>
                  <div style={{ fontSize:'12px', fontWeight:'700', color: th.green, marginBottom:'4px' }}> Your personalised experience is ready</div>
                  <div style={{ fontSize:'12px', color: th.textSec, lineHeight:1.5 }}>Based on your answers, Learnable will build you a custom curriculum map, study plan, and target your exact weak areas from day one.</div>
                </div>
                <div style={{ display:'flex', gap:'8px' }}>
                  <button onClick={()=>setStep(3)} style={{ flex:1, padding:'13px', borderRadius:'12px', fontSize:'14px', fontWeight:'600', backgroundColor:'transparent', border:`1px solid ${border}`, color:sub, cursor:'pointer' }}>← Back</button>
                  <button onClick={next} disabled={loading} style={{ flex:2, padding:'13px', borderRadius:'12px', fontSize:'15px', fontWeight:'700', background:`linear-gradient(135deg,${gold},#b8872d)`, color:'#0e0c0a', border:'none', cursor:loading?'not-allowed':'pointer', opacity:loading?0.8:1, boxShadow:'0 4px 20px rgba(212,168,67,0.3)' }}>
                    {loading ? (
                      <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                        <span style={{ width:'14px', height:'14px', border:'2px solid rgba(0,0,0,0.2)', borderTopColor:'#ffffff', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}></span>
                        Creating your account...
                      </span>
                    ) : 'Start Learning Free '}
                  </button>
                </div>
              </div>
            )}
          </div>

          <p style={{ textAlign:'center', fontSize:'11px', color: th.textTert, marginTop:'16px' }}>
            By signing up you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
